/**
 * A seven-day message chart, in inline SVG (ARCHITECTURE.md §104).
 *
 * ==================== WHY NOT A CHART LIBRARY ====================
 * §86: justify a dependency before adding one. Recharts is ~90KB gzipped and
 * pulls in D3 — for a single series of seven integers on the first page after
 * sign-in. The whole chart is about forty lines of path arithmetic, and it
 * renders with no runtime and no bundle cost.
 *
 * When Connaro has real analytics with axes, legends, tooltips and brushing, a
 * library will earn its place. One sparkline does not.
 * =================================================================
 *
 * ==================== IT IS NOT ONLY A PICTURE ====================
 * §53 and §65: a chart that exists only as a shape is unreadable to somebody
 * who cannot see it. The figures are also rendered as a table, visually
 * hidden, so a screen reader gets numbers rather than "image".
 * ==================================================================
 */

export type Point = { day: string; messages: number };

const WIDTH = 640;
const HEIGHT = 120;
const PADDING = 6;

function weekday(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

export function Sparkline({ points }: { points: readonly Point[] }): React.JSX.Element | null {
  if (points.length < 2) return null;

  /*
   * The ceiling is the largest value or 1 — never 0, which would divide by
   * zero and put every point on the axis, drawing a flat line that looks like
   * data rather than the absence of it.
   */
  const peak = Math.max(1, ...points.map((point) => point.messages));
  const step = (WIDTH - PADDING * 2) / (points.length - 1);

  const coords = points.map((point, index) => {
    const x = PADDING + index * step;
    const y = HEIGHT - PADDING - (point.messages / peak) * (HEIGHT - PADDING * 2);
    return { x, y, ...point };
  });

  const line = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
  const area = `${line} L${(WIDTH - PADDING).toFixed(1)} ${String(HEIGHT - PADDING)} L${String(PADDING)} ${String(HEIGHT - PADDING)} Z`;

  return (
    <div className="spark">
      <svg
        className="spark__svg"
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Messages per day over the last ${String(points.length)} days`}
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--c-accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--c-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={area} fill="url(#spark-fill)" />
        <path d={line} fill="none" stroke="var(--c-accent)" strokeWidth={2} strokeLinejoin="round" />

        {coords.map((c) => (
          <circle key={c.day} cx={c.x} cy={c.y} r={2.5} fill="var(--c-accent)" />
        ))}
      </svg>

      <ol className="spark__days">
        {points.map((point) => (
          <li key={point.day}>{weekday(point.day)}</li>
        ))}
      </ol>

      {/* The same data, for anyone not reading the shape. */}
      <table className="visually-hidden">
        <caption>Messages per day</caption>
        <tbody>
          {points.map((point) => (
            <tr key={point.day}>
              <th scope="row">{point.day}</th>
              <td>{point.messages}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
